/**
 * Serializes moddle Model to our server JSON format.
 * Used by saveJSON to export without XML.
 */
export function modelToJson(model) {
	if (!model) return null;
	const root = {
		identifier: model.id || null,
		name: getModelName(model),
		version: null,
	};
	if (model.elementsNode?.element?.length) {
		root.elements = model.elementsNode.element.map(el =>
			conceptToJson(el, m => {
				m.type = shortType(el.type);
			})
		);
	}
	if (model.relationshipsNode?.relationship?.length) {
		root.relationships = model.relationshipsNode.relationship.map(rel =>
			conceptToJson(rel, m => {
				m.type = rel.type ?? shortType(rel.$type);
				m.source = rel.source?.id ?? null;
				m.target = rel.target?.id ?? null;
				if (
					rel.accessType &&
					(rel.type === 'Access' || shortType(rel.$type) === 'Access')
				) {
					m.accessType = rel.accessType;
				}
			})
		);
	}
	const viewsList = model.views?.diagrams?.viewsList;
	if (viewsList?.length) {
		root.diagrams = viewsList.map(view => {
			const diag = {
				identifier: view.id,
				name: getModelName(view),
				nodes: [],
				connections: [],
			};
			const vp = view.viewpoint ?? view.$attrs?.viewpoint;
			if (vp != null && String(vp).trim() !== '') {
				diag.viewpoint = String(vp).trim();
			}
			const viewEls = view.viewElements || [];
			for (const ve of viewEls) {
				if (ve.$type && ve.$type.includes('Connection')) {
					const rel = ve.relationshipRef;
					const conn = {
						identifier: ve.id,
						type: rel ? (rel.type ?? shortType(rel.$type)) : 'Line',
						source: ve.source?.id ?? null,
						target: ve.target?.id ?? null,
						relationshipRef: rel?.id ?? null,
						bendpoints: intermediateBendpoints(ve.waypointsNode?.waypoints),
					};
					if (ve.style?.lineColor) {
						const lc = ve.style.lineColor;
						conn.lineColor = {
							r: lc.r ?? 0,
							g: lc.g ?? 0,
							b: lc.b ?? 0,
							a: lc.a ?? 100,
						};
					} else {
						conn.lineColor = {r: 0, g: 0, b: 0, a: 100};
					}
					conn.lineWidth = ve.style?.lineWidth ?? 1;
					diag.connections.push(conn);
				} else {
					diag.nodes.push(nodeToJson(ve));
				}
			}
			return diag;
		});
	}
	return root;
}

function conceptToJson(concept, extra) {
	const m = {
		identifier: concept.id,
		name: getModelName(concept),
		documentation: getModelDocumentation(concept),
	};
	if (extra) extra(m);
	const props = concept.properties?.property;
	if (props?.length) {
		m.properties = props.map(p => ({
			propertyDefinitionRef: p.propertyDefinitionRef?.id ?? p.propertyDefinitionRef,
			value: Array.isArray(p.value) ? p.value[0] : (p.value ?? null),
		}));
	}
	return m;
}

function intermediateBendpoints(waypoints) {
	if (!waypoints || waypoints.length < 3) return undefined;
	return waypoints.slice(1, -1).map(wp => ({x: wp.x, y: wp.y}));
}

function isJunctionViewElement(node) {
	const type = node.elementRef?.$type ?? node.$type ?? '';
	return (
		typeof type === 'string' && (type.includes('AndJunction') || type.includes('OrJunction'))
	);
}

function nodeToJson(node) {
	const n = {
		identifier: node.id,
		type: node.elementRef?.type || node.type || shortType(node.$type),
		x: node.x ?? 0,
		y: node.y ?? 0,
		w: node.w ?? 120,
		h: node.h ?? 60,
		elementRef: node.elementRef?.id ?? null,
	};
	if (node.label != null && node.label !== '') {
		n.label = typeof node.label === 'string' ? node.label : String(node.label);
	}
	// Junction: не сохраняем цвет — используем дефолтную палитру при отображении
	if (!isJunctionViewElement(node)) {
		if (node.style?.fillColor) {
			const fc = node.style.fillColor;
			n.fillColor = {r: fc.r ?? 255, g: fc.g ?? 255, b: fc.b ?? 255, a: fc.a ?? 100};
		}
		if (node.style?.lineColor) {
			const lc = node.style.lineColor;
			n.lineColor = {r: lc.r ?? 0, g: lc.g ?? 0, b: lc.b ?? 0, a: lc.a ?? 100};
		}
	}
	if (node.style?.lineWidth != null) n.lineWidth = node.style.lineWidth;
	if (node.style?.textAlign != null && node.style.textAlign !== '')
		n.textAlign = node.style.textAlign;
	if (node.viewElements?.length) {
		n.children = node.viewElements
			.filter(ve => !ve.$type?.includes('Connection'))
			.map(nodeToJson);
	}
	return n;
}

function getModelName(obj) {
	if (!obj) return '';
	const n = obj.name;
	if (typeof n === 'string') return n;
	if (n?.value) return String(n.value);
	return '';
}

function getModelDocumentation(obj) {
	if (!obj) return '';
	const d = obj.documentation;
	if (typeof d === 'string') return d;
	if (d?.value != null) return String(d.value);
	if (Array.isArray(d)) {
		return d
			.map(v => (v?.value != null ? String(v.value) : String(v ?? '')))
			.filter(Boolean)
			.join('\n');
	}
	return '';
}

function shortType(type) {
	if (!type || typeof type !== 'string') return 'Unknown';
	const m = type.split(':');
	return m[m.length - 1] || type;
}
